import DialogContentText from "@material-ui/core/DialogContentText";
import ActionDialog from "@saleor/components/ActionDialog";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton/ConfirmButton";
import DeleteFilterTabDialog from "@saleor/components/DeleteFilterTabDialog";
import SaveFilterTabDialog, {
  SaveFilterTabDialogFormData
} from "@saleor/components/SaveFilterTabDialog";
import {
  DEFAULT_INITIAL_PAGINATION_DATA,
  DEFAULT_INITIAL_SEARCH_DATA
} from "@saleor/config";
import useDownloadReport from "@saleor/hooks/useDownloadReport";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import useShop from "@saleor/hooks/useShop";
import { getStringOrPlaceholder, maybe } from "@saleor/misc";
import useMallSearch from "@saleor/searches/useMallSearch";
import { storeOrderPath } from "@saleor/storeOrders/urls";
import { ListViews } from "@saleor/types";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createFilterHandlers from "@saleor/utils/handlers/filterHandlers";
import Moment from "moment";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { ReportsUrl } from "../../../overview/urls";
import { InvalidThreeMonth } from "../../../utils/misc";
import { reportsDownload } from "../../../utils/utils";
import TransportReportListPage from "../../components/TransportReportListPage";
import {
  useInitialTransportReportFilterDataQuery,
  useTransportReportDownloadQuery,
  useTransportReportListQuery
} from "../../queries";
import {
  transportReportListUrl,
  TransportReportListUrlDialog,
  TransportReportUrlQueryParams
} from "../../urls";
import {
  areFiltersApplied,
  deleteFilterTab,
  getActiveFilters,
  getFilterOpts,
  getFilterQueryParam,
  getFilterTabs,
  getFilterVariables,
  saveFilterTab
} from "./filters";
interface TransportReportListProp {
  params: TransportReportUrlQueryParams;
}

const TransportReportList: React.FC<TransportReportListProp> = ({ params }) => {
  const navigate = useNavigator();
  const paginate = usePaginator();
  const shop = useShop();
  const intl = useIntl();

  const notify = useNotifier();
  const dateNow = Moment(new Date()).format("YYYYMMDDHHmmss");
  const sheetName = "Transport Report";
  const fileName = `${reportsDownload.transportReport.filename}_${dateNow}.xlsx`;

  const { updateListSettings, settings } = useListSettings(
    ListViews.REPORTS_LIST
  );
  const paginationState = createPaginationState(settings.rowNumber, params);
  const currencySymbol = shop?.defaultCurrency || "PHP";
  const [loadData, setLoadData] = useState(true);
  const [downloadPagination, setDownloadPagination] = useState("");
  const [downloadStatus, setDownloadStatus] = useState<
    ConfirmButtonTransitionState
  >("default");

  React.useEffect(() => {
    if (
      InvalidThreeMonth(params.orderDateFrom, params.orderDateTo) ||
      InvalidThreeMonth(
        params.orderCompleteDateFrom,
        params.orderCompleteDateTo
      )
    ) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage:
            "Unable to filter the report data. Please limit the date range to 3 months."
        })
      });
      navigate(transportReportListUrl());
    }
  }, [params.orderDateTo, params.orderCompleteDateTo]);

  const previewVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: getFilterVariables(params)
    }),
    [params]
  );

  const reportVariables = React.useMemo(
    () => ({
      after: downloadPagination,
      filter: getFilterVariables(params),
      first: 1000
    }),
    [params]
  );

  const { data: initialFilterData } = useInitialTransportReportFilterDataQuery({
    variables: {
      brand: params.brand,
      mall: params.mall
    }
  });

  const searchMall = useMallSearch({
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      first: 5
    }
  });

  const tabs = getFilterTabs();

  const currentTab =
    params.activeTab === undefined
      ? areFiltersApplied(params)
        ? tabs.length + 1
        : 0
      : parseInt(params.activeTab, 0);

  const [
    changeFilters,
    resetFilters,
    handleSearchChange
  ] = createFilterHandlers({
    createUrl: transportReportListUrl,
    getFilterQueryParam,
    navigate,
    params
  });

  const [openModal, closeModal] = createDialogActionHandlers<
    TransportReportListUrlDialog,
    TransportReportUrlQueryParams
  >(navigate, transportReportListUrl, params);

  const handleTabChange = (tab: number) => {
    navigate(
      transportReportListUrl({
        activeTab: tab.toString(),
        ...getFilterTabs()[tab - 1].data
      })
    );
  };

  const handleTabDelete = () => {
    deleteFilterTab(currentTab);
    navigate(transportReportListUrl());
  };

  const handleTabSave = (data: SaveFilterTabDialogFormData) => {
    saveFilterTab(data.name, getActiveFilters(params));
    handleTabChange(tabs.length + 1);
  };

  const { data: previewData } = useTransportReportListQuery({
    displayLoader: true,
    skip: Object.keys(params).length === 0,
    variables: previewVariables
  });

  const {
    data: reportData,
    loading: reportLoading
  } = useTransportReportDownloadQuery({
    displayLoader: true,
    skip: loadData,
    variables: reportVariables
  });

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    previewData?.transportReport.pageInfo,
    paginationState,
    params
  );
  React.useEffect(
    () =>
      navigate(
        transportReportListUrl({
          ...params,
          ...DEFAULT_INITIAL_PAGINATION_DATA
        }),
        true
      ),
    [settings.rowNumber]
  );

  // Todo - <Marco>: Explore on how to fix from BE
  React.useEffect(() => {
    if (params.query !== null) {
      params.after = null;
      params.before = null;
    }
  }, [params]);

  const filterOpts = getFilterOpts(params, {
    initial: maybe(
      () => initialFilterData.malls.edges.map(edge => edge.node) || []
    ),
    search: searchMall
  });

  const selectReportData = reportData => {
    const selectedData = reportData.map(data => {
      delete data.__typename;
      delete data.id;
      delete data.delivery_type;
      delete data.store_order_code;

      data.completed_at = data?.completed_at
        ? Moment.tz(new Date(data?.completed_at), "Asia/Manila").format(
            "MM/DD/YYYY HH:mm:ss"
          )
        : null;
      data.created = data?.created
        ? Moment.tz(new Date(data?.created), "Asia/Manila").format(
            "MM/DD/YYYY HH:mm:ss"
          )
        : null;
      data.canceled_at = data?.canceled_at
        ? Moment.tz(new Date(data?.canceled_at), "Asia/Manila").format(
            "MM/DD/YYYY HH:mm:ss"
          )
        : null;
      data.booked_at = data?.booked_at
        ? Moment.tz(new Date(data?.booked_at), "Asia/Manila").format(
            "MM/DD/YYYY HH:mm:ss"
          )
        : null;
      data.shipped_at = data?.shipped_at
        ? Moment.tz(new Date(data?.shipped_at), "Asia/Manila").format(
            "MM/DD/YYYY HH:mm:ss"
          )
        : null;
      data.order_collect_date = data?.order_collect_date
        ? Moment.tz(new Date(data?.order_collect_date), "Asia/Manila").format(
            "MM/DD/YYYY HH:mm:ss"
          )
        : null;
      data.rider_arrive_time = data?.rider_arrive_time
        ? Moment.tz(new Date(data?.rider_arrive_time), "Asia/Manila").format(
            "MM/DD/YYYY HH:mm:ss"
          )
        : null;
      data.shipping_schedule = data?.shipping_schedule
        ? Moment.tz(new Date(data?.shipping_schedule), "Asia/Manila").format(
            "MM/DD/YYYY HH:mm:ss"
          )
        : null;

      return data;
    });
    return selectedData;
  };

  const [downloadData, setDownloadData] = useState([]);
  const shownSales = previewData?.transportReport?.edges.map(edge => edge.node);

  const allSales = reportData?.transportReport?.edges.map(edge => edge.node);
  const nextPage = reportData?.transportReport?.pageInfo;

  React.useEffect(() => {
    if (reportData && !reportLoading) {
      setDownloadData(current => [...current, allSales]);
      setDownloadPagination(nextPage?.endCursor);
      if (!nextPage?.hasNextPage && nextPage?.endCursor === null) {
        const selectedReportData = selectReportData(downloadData?.flat());
        useDownloadReport(
          selectedReportData,
          reportsDownload.transportReport.fields,
          sheetName,
          fileName
        );
        setDownloadStatus("success");
        setDownloadPagination("");
        setLoadData(true);
        setDownloadData([]);
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Transport Report successfully downloaded."
          })
        });
        closeModal();
      }
    }
  }, [reportData]);

  const cancelDownload = () => {
    setLoadData(true);
    setDownloadData([]);
    setDownloadStatus("default");
    setDownloadPagination("");
    closeModal();
  };

  return (
    <>
      <TransportReportListPage
        transportReport={shownSales}
        currencySymbol={currencySymbol}
        currentTab={currentTab}
        filterOpts={filterOpts}
        initialSearch={params.query || ""}
        onSearchChange={handleSearchChange}
        onFilterChange={changeFilters}
        onAll={resetFilters}
        onTabChange={handleTabChange}
        onTabDelete={() => openModal("delete-search")}
        onTabSave={() => openModal("save-search")}
        tabs={tabs.map(tab => tab.name)}
        disabled={reportLoading}
        pageInfo={pageInfo}
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        onUpdateListSettings={updateListSettings}
        settings={settings}
        onBack={() => navigate(ReportsUrl)}
        download={() => openModal("download")}
        onRowClick={id => () => window.open(storeOrderPath(id))}
      />
      <ActionDialog
        open={params.action === "download"}
        onClose={cancelDownload}
        confirmButtonState={downloadStatus}
        onConfirm={() => {
          setLoadData(false);
          setDownloadStatus("loading");
          setDownloadData([]);
          setDownloadPagination("");
        }}
        variant="default"
        title={intl.formatMessage({
          defaultMessage: "Download Transport Report",
          description: "dialog title"
        })}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="You are about to download a transport report with {total} rows. Do you want to continue?"
            values={{
              total: <strong>{previewData?.transportReport?.totalCount}</strong>
            }}
          />
        </DialogContentText>
      </ActionDialog>
      <SaveFilterTabDialog
        open={params.action === "save-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabSave}
      />

      <DeleteFilterTabDialog
        open={params.action === "delete-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabDelete}
        tabName={getStringOrPlaceholder(tabs[currentTab - 1]?.name)}
      />
    </>
  );
};

export default TransportReportList;
